import { C, cardCost, roleStats } from "./accessors";
import { spendableMana } from "./mana";
import { enemiesInPressHalf } from "./occupancy";
import { teamCount } from "./state";
import type { InfiniteWarState, IwAiDecision, IwCardId, IwRoleId, IwTeam } from "./types";

/** North AI: score all 4 hand cards vs frontline / cluster / demigod / mana; light cycle follow-up. */
export function decideAiAction(state: InfiniteWarState): IwAiDecision {
  const hand = state.cards.north.hand;
  const nextCycle = state.cards.north.cycle[0];
  const north = teamCount(state, "north");
  const south = teamCount(state, "south");
  const cluster = bestEnemyCluster(state, "north");
  const halfPressure = enemiesInPressHalf(state, "north");
  const southGod = state.demigods.south;
  const demigodThreat = southGod.hp / southGod.maxHp < 0.45 ? 12 : 0;
  const available = spendableMana(state, "north");
  const manaPressure =
    available >= state.manaCap - 0.35 ? 6 : available / Math.max(1, state.manaCap);
  let best: IwAiDecision = { action: "hold", score: 0 };
  let bestScore = 2.5;

  for (const card of hand) {
    const def = C.cards[card];
    if (available < def.cost) continue;
    let score = 0;
    let targetX: number | undefined;
    let targetY: number | undefined;
    if (def.kind === "summon" && "roleId" in def && def.roleId) {
      const role = roleStats(def.roleId as IwRoleId);
      const count = "count" in def && typeof def.count === "number" ? def.count : 1;
      const elite = "elite" in def && def.elite === true;
      const same = state.units.filter(
        (u) => u.phase === "alive" && u.team === "north" && u.roleId === def.roleId,
      ).length;
      score =
        3.2 * count +
        (elite ? 7 : 0) +
        Math.max(0, south - north) * 2.2 +
        (role.tags.includes("tank") || role.tags.includes("heavy") ? (same === 0 ? 6 : 1.5) : 0) +
        (role.tags.includes("ranged") || role.tags.includes("artillery")
          ? cluster.count >= 2
            ? 4
            : 1
          : 0) +
        (role.tags.includes("dot") ? cluster.count * 1.2 : 0) +
        manaPressure * 0.7 -
        same * 1.2 -
        def.cost * 0.3;
      score += scoreSummonDeploy(def, {
        clusterCount: cluster.count,
        halfPressure,
        demigodThreat,
        allyCount: north,
        enemyCount: south,
      });
      const god = state.demigods.north;
      targetX = cluster.count > 0 ? (god.x + cluster.x) * 0.5 : god.x;
      targetY = cluster.count > 0 ? (god.y + cluster.y) * 0.5 : god.y + 90;
    } else if (
      def.kind === "shockwave" ||
      def.kind === "cone" ||
      def.kind === "zone" ||
      def.kind === "delayed_blast" ||
      def.kind === "curse_cluster" ||
      def.kind === "sacrifice_nuke"
    ) {
      // Spell payoff is superlinear in cluster size (dense boards justify mid/high costs).
      const clusterMul =
        def.kind === "delayed_blast" || def.kind === "sacrifice_nuke"
          ? 3.1
          : def.kind === "curse_cluster"
            ? 2.4
            : def.kind === "zone"
              ? 1.6
              : 2.0;
      score =
        cluster.count * 7.2 +
        cluster.count * cluster.count * clusterMul +
        (south - north) * 1.3 +
        demigodThreat * 0.55;
      if (cluster.count < 2) score -= 18;
      if (def.kind === "sacrifice_nuke" && north < 1) score -= 40;
      targetX = cluster.x;
      targetY = cluster.y;
    } else if (def.kind === "board_half") {
      // Coverage compounds with press-half density — not linear dmg×cost.
      const halfDmg = C.shapes.board_half.damage;
      score =
        halfPressure * 4.2 +
        halfPressure * halfPressure * 2.8 +
        halfDmg * 0.22 * Math.min(halfPressure, 6) +
        demigodThreat * 0.6 +
        (south - north) * 1.1;
      if (halfPressure < 2) score -= 16;
    }
    if (nextCycle) {
      const leftover = available - def.cost;
      if (leftover >= cardCost(nextCycle as IwCardId)) score += 1.4;
    }
    if (score <= bestScore) continue;
    bestScore = score;
    best = { action: "play", card, score, targetX, targetY };
  }
  return best;
}

function scoreSummonDeploy(
  def: (typeof C.cards)[IwCardId],
  ctx: {
    clusterCount: number;
    halfPressure: number;
    demigodThreat: number;
    allyCount: number;
    enemyCount: number;
  },
): number {
  if (!("deploy" in def) || !def.deploy) return 0;
  const kind = (def.deploy as { kind: string }).kind;
  // Deploy merit: compete when fronts/demigod need bodies — not when a dense AoE cluster is free.
  const clusterCap = Math.min(ctx.clusterCount, 2);
  const outnumbered = Math.max(0, ctx.enemyCount - ctx.allyCount);
  let score = 3.2;
  if (kind === "opening_volley" || kind === "blight_seed") {
    score += clusterCap * 1.6 + ctx.halfPressure * 1.5 + ctx.demigodThreat * 0.45 + outnumbered * 1.1;
  } else if (kind === "banner" || kind === "shield_pulse") {
    score +=
      ctx.allyCount * 0.65 +
      outnumbered * 1.5 +
      (ctx.halfPressure >= 2 ? 2.5 : 0) +
      ctx.demigodThreat * 0.25;
  } else if (kind === "judgment_slam" || kind === "blood_rite") {
    score +=
      clusterCap * 2.0 +
      ctx.halfPressure * 1.4 +
      ctx.demigodThreat * 0.85 +
      outnumbered * 0.8 +
      ("elite" in def && def.elite ? 3 : 0);
  }
  if (ctx.clusterCount >= 3) score -= 11;
  return score;
}

export function bestEnemyCluster(
  state: InfiniteWarState,
  team: IwTeam,
): { x: number; y: number; count: number } {
  const enemies = state.units.filter((u) => u.phase === "alive" && u.team !== team);
  const probeR = C.shapes.zone.radius * 1.15;
  let best = { x: 270, y: team === "north" ? 560 : 400, count: 0 };
  for (const center of enemies) {
    const group = enemies.filter(
      (u) => Math.hypot(u.x - center.x, u.y - center.y) <= probeR,
    );
    if (group.length <= best.count) continue;
    best = {
      x: group.reduce((sum, u) => sum + u.x, 0) / group.length,
      y: group.reduce((sum, u) => sum + u.y, 0) / group.length,
      count: group.length,
    };
  }
  return best;
}
